<script setup>
import { ref, watch } from 'vue';

const WEEKDAYS = [
  { key: 'monday', label: 'Seg' },
  { key: 'tuesday', label: 'Ter' },
  { key: 'wednesday', label: 'Qua' },
  { key: 'thursday', label: 'Qui' },
  { key: 'friday', label: 'Sex' },
  { key: 'saturday', label: 'Sáb' },
  { key: 'sunday', label: 'Dom' },
];

const LOCATION_TYPES = [
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'presential', label: 'Presencial' },
  { value: 'phone', label: 'Telefone' },
  { value: 'webhook', label: 'Webhook' },
];

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
];

const props = defineProps({
  schedule: { type: Object, default: null },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save', 'close']);

// ─── Products ─────────────────────────────────────────────
const products = ref([]);
const newProduct = ref('');

// ─── Appointment types ────────────────────────────────────
const appointmentTypes = ref([]);
const newAppointmentType = ref('');

const defaultAvailability = () => ({
  monday: [{ start: '09:00', end: '18:00' }],
  tuesday: [{ start: '09:00', end: '18:00' }],
  wednesday: [{ start: '09:00', end: '18:00' }],
  thursday: [{ start: '09:00', end: '18:00' }],
  friday: [{ start: '09:00', end: '18:00' }],
  saturday: [],
  sunday: [],
});

const form = ref({
  title: '',
  slug: '',
  description: '',
  color: '#6366f1',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
  location_type: 'google_meet',
  duration_minutes: 30,
  buffer_minutes: 0,
  slot_capacity: 1,
  availability_rules: defaultAvailability(),
});

watch(
  () => props.schedule,
  schedule => {
    if (schedule) {
      form.value = {
        title: schedule.title || '',
        slug: schedule.slug || '',
        description: schedule.description || '',
        color: schedule.color || '#6366f1',
        timezone: schedule.timezone || 'America/Sao_Paulo',
        location_type: schedule.location_type || 'google_meet',
        duration_minutes: schedule.duration_minutes || 30,
        buffer_minutes: schedule.buffer_minutes || 0,
        slot_capacity: schedule.slot_capacity || 1,
        availability_rules: schedule.availability_rules || defaultAvailability(),
      };
      products.value = [...(schedule.integration_settings?.products || [])];
      appointmentTypes.value = [...(schedule.integration_settings?.appointment_types || [])];
    } else {
      form.value = {
        title: '',
        slug: '',
        description: '',
        color: '#6366f1',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
        location_type: 'google_meet',
        duration_minutes: 30,
        buffer_minutes: 0,
        slot_capacity: 1,
        availability_rules: defaultAvailability(),
      };
      products.value = [];
      appointmentTypes.value = [];
    }
  },
  { immediate: true }
);

const addProduct = () => {
  const name = newProduct.value.trim();
  if (!name || products.value.includes(name)) return;
  products.value.push(name);
  newProduct.value = '';
};

const removeProduct = i => products.value.splice(i, 1);

const addAppointmentType = () => {
  const name = newAppointmentType.value.trim();
  if (!name || appointmentTypes.value.includes(name)) return;
  appointmentTypes.value.push(name);
  newAppointmentType.value = '';
};

const removeAppointmentType = i => appointmentTypes.value.splice(i, 1);

// Auto slug from title
watch(
  () => form.value.title,
  title => {
    if (!props.schedule) {
      form.value.slug = title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  }
);

const isDayOpen = day => {
  const rules = form.value.availability_rules[day] || [];
  return rules.length > 0;
};

const toggleDay = day => {
  if (isDayOpen(day)) {
    form.value.availability_rules[day] = [];
  } else {
    form.value.availability_rules[day] = [{ start: '09:00', end: '18:00' }];
  }
};

const handleSubmit = () => {
  if (!form.value.title.trim() || props.isSaving) return;
  emit('save', {
    ...form.value,
    integration_settings: {
      ...(props.schedule?.integration_settings || {}),
      products: products.value,
      appointment_types: appointmentTypes.value,
    },
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="emit('close')" />

      <div
        class="relative bg-n-background rounded-2xl shadow-2xl border border-n-weak w-full max-w-xl max-h-[90vh] overflow-y-auto z-10"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ schedule ? 'Editar agenda' : 'Nova agenda' }}
            </h2>
            <p class="text-xs text-n-slate-9 mt-0.5">
              Configure nome, duração, slots e disponibilidade.
            </p>
          </div>
          <button
            class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <form class="px-6 py-5 space-y-6" @submit.prevent="handleSubmit">

          <!-- ─── Identity ─── -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
                Identidade
              </span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <!-- Color picker -->
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-2">Cor</label>
              <div class="flex items-center gap-2 flex-wrap">
                <button
                  v-for="color in PALETTE"
                  :key="color"
                  type="button"
                  class="w-7 h-7 rounded-full border-2 transition-all"
                  :class="form.color === color ? 'border-n-slate-12 scale-110' : 'border-transparent'"
                  :style="{ backgroundColor: color }"
                  @click="form.color = color"
                />
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Nome da agenda <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.title"
                type="text"
                placeholder="Ex: Consulta Médica, Reunião de Vendas..."
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Descrição</label>
              <textarea
                v-model="form.description"
                rows="2"
                placeholder="Descreva o tipo de atendimento..."
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all resize-none"
              />
            </div>

            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Slug</label>
                <input
                  v-model="form.slug"
                  type="text"
                  placeholder="minha-agenda"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all font-mono"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Tipo de atendimento</label>
                <select
                  v-model="form.location_type"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                >
                  <option v-for="loc in LOCATION_TYPES" :key="loc.value" :value="loc.value">
                    {{ loc.label }}
                  </option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Fuso horário</label>
              <input
                v-model="form.timezone"
                type="text"
                placeholder="America/Sao_Paulo"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all font-mono"
              />
            </div>
          </div>

          <!-- ─── Capacity ─── -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
                Capacidade
              </span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <div class="grid grid-cols-3 gap-2.5">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Duração (min)</label>
                <input
                  v-model.number="form.duration_minutes"
                  type="number"
                  min="5"
                  step="5"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Intervalo (min)</label>
                <input
                  v-model.number="form.buffer_minutes"
                  type="number"
                  min="0"
                  step="5"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Vagas/horário</label>
                <input
                  v-model.number="form.slot_capacity"
                  type="number"
                  min="1"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
            </div>

            <div
              class="flex items-center gap-3 px-4 py-3 bg-n-alpha-1 rounded-xl border border-n-weak"
            >
              <span class="i-lucide-info size-3.5 text-n-slate-9" />
              <p class="text-xs text-n-slate-9 leading-relaxed">
                Cada slot terá <strong class="text-n-slate-11">{{ form.duration_minutes }}min</strong>
                de duração com
                <strong class="text-n-slate-11">{{ form.slot_capacity }} vaga{{ form.slot_capacity !== 1 ? 's' : '' }}</strong>
                simultâneas e <strong class="text-n-slate-11">{{ form.buffer_minutes }}min</strong> de intervalo.
              </p>
            </div>
          </div>

          <!-- ─── Availability ─── -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
                Disponibilidade semanal
              </span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <div class="space-y-2">
              <div
                v-for="day in WEEKDAYS"
                :key="day.key"
                class="flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all"
                :class="
                  isDayOpen(day.key)
                    ? 'border-n-brand/30 bg-n-brand/5'
                    : 'border-n-weak bg-n-background'
                "
              >
                <!-- Toggle -->
                <button
                  type="button"
                  class="relative w-9 h-5 rounded-full transition-colors flex-shrink-0"
                  :class="isDayOpen(day.key) ? 'bg-n-brand' : 'bg-n-slate-5'"
                  @click="toggleDay(day.key)"
                >
                  <span
                    class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform"
                    :class="isDayOpen(day.key) ? 'translate-x-4' : 'translate-x-0'"
                  />
                </button>

                <span
                  class="w-8 text-xs font-semibold"
                  :class="isDayOpen(day.key) ? 'text-n-brand' : 'text-n-slate-8'"
                >
                  {{ day.label }}
                </span>

                <template v-if="isDayOpen(day.key) && form.availability_rules[day.key]?.length > 0">
                  <div class="flex items-center gap-1.5 flex-1">
                    <input
                      v-model="form.availability_rules[day.key][0].start"
                      type="time"
                      class="flex-1 px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    />
                    <span class="text-n-slate-8 text-xs">até</span>
                    <input
                      v-model="form.availability_rules[day.key][0].end"
                      type="time"
                      class="flex-1 px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    />
                  </div>
                </template>

                <span
                  v-if="!isDayOpen(day.key)"
                  class="flex-1 text-xs text-n-slate-7"
                >
                  Fechado
                </span>
              </div>
            </div>
          </div>

          <!-- ─── Produtos ─── -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Produtos</span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <p class="text-xs text-n-slate-8">
              Defina os produtos/serviços disponíveis nesta agenda. Eles aparecerão como opção ao criar um agendamento.
            </p>

            <div class="flex gap-2">
              <input
                v-model="newProduct"
                type="text"
                placeholder="Nome do produto ou serviço..."
                class="flex-1 px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                @keydown.enter.prevent="addProduct"
              />
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 transition-all"
                @click="addProduct"
              >
                <span class="i-lucide-plus size-3.5" />
                Adicionar
              </button>
            </div>

            <div v-if="products.length > 0" class="flex flex-wrap gap-2">
              <span
                v-for="(product, i) in products"
                :key="i"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-2 border border-n-weak rounded-xl text-xs text-n-slate-11"
              >
                {{ product }}
                <button
                  type="button"
                  class="text-n-slate-8 hover:text-red-500 transition-colors"
                  @click="removeProduct(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>

            <p v-else class="text-xs text-n-slate-7 italic">Nenhum produto cadastrado.</p>
          </div>

          <!-- ─── Tipos de Agendamento ─── -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Tipos de Agendamento</span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <p class="text-xs text-n-slate-8">
              Defina os tipos de agendamento disponíveis nesta agenda. Eles aparecerão como opção ao criar um agendamento.
            </p>

            <div class="flex gap-2">
              <input
                v-model="newAppointmentType"
                type="text"
                placeholder="Ex: Consulta, Retorno, Avaliação..."
                class="flex-1 px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                @keydown.enter.prevent="addAppointmentType"
              />
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 transition-all"
                @click="addAppointmentType"
              >
                <span class="i-lucide-plus size-3.5" />
                Adicionar
              </button>
            </div>

            <div v-if="appointmentTypes.length > 0" class="flex flex-wrap gap-2">
              <span
                v-for="(type, i) in appointmentTypes"
                :key="i"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-2 border border-n-weak rounded-xl text-xs text-n-slate-11"
              >
                {{ type }}
                <button
                  type="button"
                  class="text-n-slate-8 hover:text-red-500 transition-colors"
                  @click="removeAppointmentType(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>

            <p v-else class="text-xs text-n-slate-7 italic">Nenhum tipo cadastrado.</p>
          </div>

          <!-- ─── Footer ─── -->
          <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-xl transition-colors"
              @click="emit('close')"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="!form.title.trim() || isSaving"
              class="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <span v-if="isSaving" class="i-lucide-loader-2 size-3.5 animate-spin" />
              <span v-else class="i-lucide-check size-3.5" />
              {{ isSaving ? 'Salvando...' : schedule ? 'Atualizar agenda' : 'Criar agenda' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
